import { Close } from '@mui/icons-material';
import {
  Fade,
  Typography,
  IconButton,
  Box,
  Modal,
  Link,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert
} from '@mui/material';
import { useState, useEffect } from 'react';
import MarkdownContent from '~/components/MarkdownContent';
import { getOrganizationTermsOfUse, TermType } from '~/services/modules/organizationTermsOfUse';
import { containerTermOfContract, modalContainer, termOfContract, termOfContractLabel } from '~/pages/Payment/styles';

interface ModalTermOfContractProps {
  checkedTermOfContract: boolean;
  setCheckedTermOfContract: (value: boolean) => void;
  organizationId?: string | null;
  /** Texto do link de aceite. Padrão: "termos de uso". */
  linkLabel?: string;
  /** Tipo do termo buscado na API (rota /OrganizationTermsOfUse/{termType}). Padrão: "General". */
  termType?: TermType;
}

export default function ModalTermOfContract({
  checkedTermOfContract,
  setCheckedTermOfContract,
  organizationId,
  linkLabel = 'termos de uso',
  termType = 'General'
}: ModalTermOfContractProps) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    getOrganizationTermsOfUse(termType, organizationId)
      .then(setContent)
      .catch((err) => {
        setError(err?.message ?? 'Não foi possível carregar os termos de uso.');
        setContent('');
      })
      .finally(() => setLoading(false));
  }, [open, organizationId, termType]);

  return (
    <>
      <Box sx={containerTermOfContract}>
        <FormControlLabel
          sx={{ marginRight: 0.4 }}
          control={
            <Checkbox
              checked={checkedTermOfContract}
              onChange={() => setCheckedTermOfContract(!checkedTermOfContract)}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={termOfContractLabel}>Li e concordo com os </Typography>
              <Link sx={termOfContract} underline="hover" onClick={handleOpen}>
                {linkLabel}
              </Link>
            </Box>
          }
        />
        <Modal open={open} onClose={handleClose}>
          <Fade in={open}>
            <Box sx={{ ...modalContainer, position: 'relative' }}>
              <IconButton
                color="default"
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: { xs: 8, sm: 16 },
                  right: { xs: 8, sm: 16 },
                  zIndex: 1
                }}
              >
                <Close />
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: { xs: 1, sm: 2 },
                  paddingTop: { xs: 1, sm: 2 },
                  gap: 3
                }}
              >
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                )}
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}
                {!loading &&
                  !error &&
                  (content?.trim() ? (
                    <MarkdownContent content={content} />
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Conteúdo não disponível no momento.
                    </Typography>
                  ))}
              </Box>
            </Box>
          </Fade>
        </Modal>
      </Box>
    </>
  );
}
